import { message } from 'antd';
import { UploadFile } from 'antd/lib/upload/interface';
import { Button, FormItem } from 'components';
import Divider from 'containers/DriverPortalPage/components/Divider';
import DriverPortalLayout from 'containers/DriverPortalPage/components/DriverPortalLayout';
import { FormikProps, FormikValues, useFormik } from 'formik';
import usePrompt from 'hooks/usePrompt';
import { useQueryParams } from 'hooks/useQueryParams';
import React, { useCallback, useEffect, useState } from 'react';
import {
  addressService,
  authenticationService,
  documentReaderService,
  mainService,
} from 'services/driverPortal';
import { ReaderType } from 'services/driverPortal/documentReader/DocumentReaderService';
import { isPdfFile } from 'utils/isFilePdf';
import * as Yup from 'yup';
import Loading from '../../../../components/ReturnOrderModal/Loading';
import ConfirmReturnModal from '../components/ConfirmReturnModal';
import HeaderPortal from '../components/HeaderPortal';
import Information from '../components/Information';
import ReturnButton from '../components/ReturnButton';
import UploadImageForm from '../components/UploadImageForm';
import InsuranceExampleImage from '../../../../assets/images/driver-portal/preview/Insurance-Example.png';

interface IInsuranceUploadRequest {
  document: { image?: string };
  fileData?: any;
}

interface IInsuranceFiles {
  document?: FormData;
}

const folderType = 'voluntary-insurance';

const validateSchema = Yup.object().shape({
  document: Yup.object().shape({
    image: Yup.string().required('กรุณาอัพโหลดเอกสารประกัน'),
  }),
});

const VoluntaryInsuranceUploadPage: React.FC = () => {
  const { search, pushUrl } = useQueryParams();
  const [isShowModal, setIsShowModal] = useState(false);
  const [, setShowPrompt] = usePrompt();
  const [formDirty, setFormDirty] = useState(false);
  const [imageDeletionFiles, setImageDeletionFiles] = useState<UploadFile[]>(
    [],
  );
  const [voluntaryInsuranceFileName, setVoluntaryInsuranceFileName] = useState(
    '',
  );
  const [filesToUpload, setFilesToUpload] = useState<IInsuranceFiles>({});
  const [saveLoading, setSaveLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [isApproved, setIsApproved] = useState(false);
  const goToMainPage = () => pushUrl('/', search);

  useEffect(() => {
    const { accessToken } = authenticationService.getUserToken() || {};
    if (!accessToken) pushUrl('/login', search);
  }, [pushUrl, search]);

  const formik = useFormik<IInsuranceUploadRequest>({
    initialValues: {
      document: { image: undefined },
      fileData: null,
    },
    validationSchema: validateSchema,
    enableReinitialize: true,
    onSubmit: async formValues => {
      if (saveLoading) return;
      setSaveLoading(true);
      try {
        const f = formValues.fileData ?? {};
        const fd = new FormData();
        fd.append('tab', 'voluntary_insurance');
        fd.append('companyName', f.company ?? f.insurance_company_name ?? '');
        fd.append(
          'insurancePolicyNumber',
          f.insurance_policy_number ?? f.policy_number ?? '',
        );
        fd.append('insuredName', f.insured_name ?? f.policy_holder_name ?? '');
        fd.append('startDate', f.start_date ?? f.insurance_start_date ?? '');
        fd.append('endDate', f.end_date ?? f.insurance_end_date ?? '');
        fd.append('brand', f.brand ?? f.vehicle_brand_en ?? '');
        fd.append('model', f.model ?? f.vehicle_model_name ?? '');
        fd.append(
          'licensePlate',
          f.license_plate ??
            f.license_plate_number ??
            f.vehicle_license_number ??
            '',
        );
        fd.append(
          'chassisNumber',
          f.chassis_number ?? f.vehicle_chassis_number ?? '',
        );
        fd.append(
          'engineNumber',
          f.engine_number ?? f.vehicle_engine_number ?? '',
        );
        fd.append('serialNumber', f.serial_number ?? '');
        fd.append('year', f.year ?? f.vehicle_model_year ?? '');
        fd.append('typeEngine', f.type_engine ?? f.engine_type ?? '');

        if (filesToUpload.document?.get('files')) {
          fd.append(
            'voluntaryInsuranceImage',
            filesToUpload.document.get('files') ?? '',
          );
        } else if (voluntaryInsuranceFileName && !formValues.document.image) {
          fd.append('voluntaryInsuranceImage', '');
        }

        const res = await addressService.updateInfo(fd);
        const out = (res as any)?.data ?? res;

        if (out?.success === false) {
          const msg =
            out?.errorMessageList?.[0] || out?.message || 'บันทึกไม่สำเร็จ';
          message.error(msg);
          return;
        }

        message.success('บันทึกข้อมูลเรียบร้อยแล้ว');
        goToMainPage();
      } finally {
        setSaveLoading(false);
      }
    },
  });

  const ocrVoluntaryInsurance = async (file: File) => {
    try {
      const res: any = await documentReaderService.readerFile(
        file,
        ReaderType.RIDER_VOLUNTARY_MOTORCYCLE_INSURANCE,
      );
      if (res.status === false) {
        formik.setFieldValue('fileData', {});
      } else {
        formik.setFieldValue('fileData', res);
      }
    } catch (e) {
      formik.setFieldValue('fileData', {});
      console.error('[ocr:voluntary_insurance] error:', e);
    }
  };

  useEffect(() => {
    const { dirty, isSubmitting } = formik;
    const isDirty = !isSubmitting && dirty;
    setFormDirty(isDirty);
    setIsShowModal(isDirty);
    setShowPrompt(isDirty);
  }, [formik]);

  const handleRemoveImage = useCallback(
    (file: UploadFile) => {
      if (!file.fileName?.match(/^blob\:/)) {
        setImageDeletionFiles(prev => [...prev, file]);
      }
      formik.setFieldValue('document', { image: undefined });
      formik.setFieldValue('fileData', undefined);
    },
    [imageDeletionFiles],
  );

  useEffect(() => {
    const getInfo = async () => {
      try {
        const response = await mainService.getInfo();
        const lead = response?.data?.potentialLead;
        const leadId = lead?.potentialLeadId ?? lead?.id ?? lead?._id ?? '';
        const data = (response?.data as any)?.potentialLeadVoluntaryInsurance;
        if (data?.statusCode === 'approved') {
          setIsApproved(true);
        }

        if (data?.voluntaryInsuranceImage) {
          const image = await mainService.loadImageAxios(
            data.voluntaryInsuranceImage,
          );
          setVoluntaryInsuranceFileName(data.voluntaryInsuranceImage);
          formik.setFieldValue('document', { image: image?.url });
          formik.setFieldValue('fileData', {});
        }
      } catch (e) {
        console.log('getInfo error:', e);
      } finally {
        setPageLoading(false);
      }
    };
    getInfo();
  }, []);

  const errors = formik.errors || {};
  const isOcrLoading =
    !!formik.values.document.image && !formik.values.fileData;
  const isSubmitDisabled =
    !formik.values.document.image || isOcrLoading || saveLoading;

  return (
    <>
      {(pageLoading || isOcrLoading) && <Loading />}
      <form onSubmit={formik.handleSubmit}>
        <HeaderPortal formik={formik as FormikProps<FormikValues>} />
        <DriverPortalLayout>
          <ReturnButton />

          <Information
            title="ประกันภาคสมัครใจ"
            description='กรุณากรอกข้อมูล และอัพโหลดเอกสาร แล้วกด "บันทึก"'
          />

          <Divider />

          <FormItem
            labelCol={{ span: 24 }}
            colon={false}
            validateStatus={errors.document?.image ? 'error' : ''}
            help={errors.document?.image}
          >
            <UploadImageForm
              disabled={isApproved}
              title="อัพโหลดประกัน"
              bulletList={[
                'ไม่แก้ไขและตกแต่งรูป',
                'เห็นข้อมูลชัดเจนและครบถ้วน',
              ]}
              previewTitle="ตัวอย่างประกันภาคสมัครใจ"
              previewUrl={InsuranceExampleImage}
              onChange={value => {
                const file = value.uploadFileForm?.get('files') as
                  | File
                  | undefined;
                setFilesToUpload({ document: value.uploadFileForm });
                setVoluntaryInsuranceFileName(file?.name || '');
                formik.setFieldValue('document', {
                  image: file ? URL.createObjectURL(file) : undefined,
                });
                if (file) {
                  ocrVoluntaryInsurance(file);
                }
              }}
              pathImage={formik.values.document.image}
              nameImage="ประกันภาคสมัครใจ"
              onRemove={handleRemoveImage}
              folder={folderType}
              isPdf={isPdfFile(voluntaryInsuranceFileName)}
              fileName={voluntaryInsuranceFileName}
            />
          </FormItem>

          <Divider />

          <Button
            block
            type={isSubmitDisabled || isApproved ? 'default' : 'primary'}
            size="large"
            disabled={isSubmitDisabled || isApproved}
            htmlType="submit"
            loading={saveLoading}
          >
            บันทึก
          </Button>
        </DriverPortalLayout>
        {formDirty && <ConfirmReturnModal isShow={isShowModal} />}
      </form>
    </>
  );
};

export default VoluntaryInsuranceUploadPage;
